import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../ui/dialog';
import { API, AuthContext } from '@/App';
import { isGlobalAdmin } from '@/lib/workspaces';
import { ShieldCheck, Clock, Pencil, BadgeCheck, FileText, Save } from 'lucide-react';
import ServiceSlices from './ServiceSlices';
import { policyStatus, POLICY_STATUS_META } from '@/lib/providerDivisions';
import ProviderDocuments from './ProviderDocuments';

const CHECKLIST = [
  ['company_info', 'Company information verified'],
  ['contact_info', 'Contact information verified'],
  ['services', 'Services confirmed'],
  ['coverage', 'Geographic coverage confirmed'],
  ['w9', 'W-9 on file & verified'],
  ['coi', 'Certificate of insurance on file & current'],
  ['trade_docs', 'Trade-specific documentation (if applicable)'],
];
const REQUIRED = ['company_info', 'contact_info', 'services', 'coverage'];  // docs (w9/coi) enforced via verified uploads, not a checkbox

export function QualificationBadge({ provider }) {
  const qualified = provider?.qualification_status === 'qualified';
  return (
    <span data-testid="provider-qual-badge" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${qualified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
      {qualified ? <BadgeCheck size={12} /> : <Clock size={12} />}
      {qualified ? 'Qualified Service Provider' : 'Registered / Pending'}
    </span>
  );
}

const isEmptyContent = (c) => c == null || c === '' || c === false
  || (Array.isArray(c) && c.every((x) => isEmptyContent(x)));
const Row = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100 text-sm">
    <div className="text-slate-500">{label}</div>
    <div className="col-span-2 text-slate-800 break-words">{isEmptyContent(children) ? '—' : children}</div>
  </div>
);

export default function ProviderDetailCard({ provider: initialProvider, onClose, onEdit, onChanged }) {
  const { token, user } = useContext(AuthContext);
  const [provider, setProvider] = useState(initialProvider);
  const canApprove = isGlobalAdmin(user);
  const canManageDocs = ['owner', 'admin', 'manager'].includes(user?.role) || isGlobalAdmin(user);
  const qualified = provider?.qualification_status === 'qualified';
  const [checklist, setChecklist] = useState(() => ({ ...(provider?.qualification_checklist || {}) }));
  const [busy, setBusy] = useState(false);
  const docs = provider?.documents || [];
  const docOk = (t) => docs.some((d) => (d.type || '').toLowerCase() === t
    && (d.verification_status === 'verified')
    && (!d.expiry || d.expiry >= new Date().toISOString().slice(0, 10)));
  const missingReq = REQUIRED.filter((k) => !checklist[k]);
  const coiMode = provider.coi_requirement === 'tfsi_provided' ? 'tfsi_provided' : 'provider_supplied';
  const coiRequired = coiMode === 'provider_supplied';
  const coiSetupPending = coiMode === 'tfsi_provided' ? !provider.coi_requirement_set_by : false;
  const docTypesReq = ['w-9', "driver's license", 'nda', ...(coiRequired ? ['coi'] : [])];
  const docMissing = docTypesReq.filter((t) => !docOk(t));
  const notify = (updated) => { if (updated) setProvider(updated); onChanged?.(); };

  // ---- Outstanding requirements (grouped) ----
  const today = new Date().toISOString().slice(0, 10);
  const docState = (t) => {
    const d = docs.find((x) => (x.type || '').toLowerCase() === t);
    if (!d) return 'missing';
    if (d.expiry && d.expiry < today) return 'expired';
    if ((d.verification_status || 'pending') !== 'verified') return 'unverified';
    return 'ok';
  };
  const INFO_LABELS = { company_info: 'Company information', contact_info: 'Contact information', services: 'Services', coverage: 'Geographic coverage' };
  const overrideLabel = (o) => {
    if (o.startsWith('info:')) return `Missing information — ${INFO_LABELS[o.slice(5)] || o.slice(5)}`;
    if (o.startsWith('doc-missing:')) return `Missing document — ${o.slice(12)}`;
    if (o.startsWith('doc-unverified:')) return `Unverified document — ${o.slice(15)}`;
    if (o.startsWith('doc-expired:')) return `Expired document — ${o.slice(12)}`;
    return o;
  };
  const missingInfo = REQUIRED.filter((k) => !['w9', 'coi'].includes(k) && !checklist[k]);
  const docMissingList = docTypesReq.filter((t) => docState(t) === 'missing');
  const docUnverified = docTypesReq.filter((t) => docState(t) === 'unverified');
  const docExpired = docTypesReq.filter((t) => docState(t) === 'expired');
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((provider.contact_email || '').trim());
  const overridden = (provider.qualification_exception && provider.qualification_exception.overridden) || [];
  const allClear = missingInfo.length === 0 && docMissingList.length === 0 && docUnverified.length === 0 && docExpired.length === 0 && !coiSetupPending && emailValid;

  const [showException, setShowException] = useState(false);
  const [exReason, setExReason] = useState('');
  const [exAck, setExAck] = useState(false);

  async function setCoiRequirement(mode) {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/service-providers/${provider.id}/coi-requirement`,
        { mode, confirmed: mode === 'tfsi_provided' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('COI requirement updated');
      notify(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not update COI requirement'));
    } finally { setBusy(false); }
  }

  async function approveException() {
    if (!emailValid) { toast.error('Email address required for qualification — it cannot be waived'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-providers/${provider.id}/qualify-exception`,
        { reason: exReason, acknowledged: exAck }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Qualified by exception — ${r.data.qsp_number} assigned`);
      setShowException(false); setExReason(''); setExAck(false); notify(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not approve by exception'));
    } finally { setBusy(false); }
  }

  async function approve() {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-providers/${provider.id}/qualify`, { checklist },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Qualified — ${r.data.qsp_number} assigned`);
      notify(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not qualify'));
    } finally { setBusy(false); }
  }

  async function saveReview() {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-providers/${provider.id}/review`, { checklist },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Review progress saved');
      notify(r.data);
    } catch { toast.error('Could not save review'); } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="provider-detail-card"
        onInteractOutside={(e) => e.preventDefault()}>
        <div className="flex items-start justify-between gap-3 pr-8">
          <div className="flex items-start gap-3">
            <ServiceSlices provider={provider} size={44} />
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-2xl font-extrabold text-[#0E2A47]" data-testid="provider-detail-name">{provider.company_name}</h2>
                {provider.qsp_number && (
                  <span data-testid="provider-detail-qsp" className="rounded-md bg-[#0E2A47] px-2 py-0.5 text-xs font-bold text-white">{provider.qsp_number}</span>
                )}
                <QualificationBadge provider={provider} />
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${(provider.status || 'active') === 'active' ? 'bg-slate-100 text-slate-700' : 'bg-rose-100 text-rose-700'}`}>{(provider.status || 'active') === 'active' ? 'Active' : 'Inactive'}</span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{provider.provider_type} · {(provider.divisions && provider.divisions.length ? provider.divisions : provider.trade_types || []).join(', ') || 'No services listed'}</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button onClick={() => onEdit(provider)} data-testid="provider-detail-edit"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium hover:bg-slate-50"><Pencil size={14} />Edit</button>
          </div>
        </div>

        {provider.qualification_recheck?.flagged && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="provider-recheck-flag">
            <span className="font-semibold">Flagged for compliance re-check</span> by {provider.qualification_recheck.by_name}
            {provider.qualification_recheck.at ? ` · ${String(provider.qualification_recheck.at).slice(0, 10)}` : ''}
            {provider.qualification_recheck.note ? ` — "${provider.qualification_recheck.note}"` : ''}
          </div>
        )}

        <div className="mt-3">
          <Row label="Primary contact">{provider.contact_name}{provider.contact_email ? ` · ${provider.contact_email}` : ''}{provider.contact_phone ? ` · ${provider.contact_phone}` : ''}</Row>
          <Row label="Secondary contact">{provider.secondary_contact_name}{provider.secondary_contact_email ? ` · ${provider.secondary_contact_email}` : ''}</Row>
          <Row label="Business address">{[provider.address, provider.city, provider.state, provider.zip].filter(Boolean).join(', ')}</Row>
          <Row label="Website">{provider.website}</Row>
          <Row label="Services">{[...(provider.divisions || []), ...((provider.trade_types || []).filter((t) => !(provider.divisions || []).includes(t)))].join(', ')}{provider.specialties ? ` — ${provider.specialties}` : ''}</Row>
          <Row label="Specialty trades">{(provider.specialty_trades && provider.specialty_trades.length) ? provider.specialty_trades.join(', ') : <span className="italic text-slate-400">Not specified</span>}</Row>
          <Row label="Coverage — states">{(provider.service_states || []).join(', ')}</Row>
          <Row label="Coverage — counties">{(provider.service_counties || []).join(', ')}{(provider.partial_counties || []).length ? ` (partial: ${provider.partial_counties.join(', ')})` : ''}</Row>
          <Row label="Coverage — cities/regions">{[...(provider.service_cities || []), ...(provider.service_regions || [])].join(', ')}</Row>
          <Row label="P1 emergency">{provider.accepts_p1 === true ? 'Accepts P1 emergencies' : provider.accepts_p1 === false ? 'Does not accept P1' : <span className="italic text-slate-400">Unknown — not specified</span>}</Row>
          <Row label="Response commitments">{(provider.priority_commitments || []).length ? provider.priority_commitments.map((c, i) => <span key={i} className="mr-2">{c.priority}/{c.service || 'any'}/{c.area || 'any'}{c.response_hours ? ` (${c.response_hours}h)` : ''}</span>) : <span className="italic text-slate-400">Unknown — not specified</span>}</Row>
          {qualified && <Row label="Qualified by">{provider.qualification_approved_by_name} · {provider.qualification_approved_at ? String(provider.qualification_approved_at).slice(0, 10) : ''}</Row>}
          <Row label="Notes">{provider.notes}</Row>
        </div>

        {/* Services & Agreed Rates (provider payouts) */}
        <div className="mt-4" data-testid="provider-rates-block">
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-[#8FC93A]">Services &amp; Agreed Rates</div>
          {(provider.service_rates || []).length ? (
            <div className="overflow-x-auto rounded-md border border-slate-100">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500"><tr>
                  <th className="px-2 py-1 text-left">Service</th><th className="px-2 py-1 text-left">Priority</th>
                  <th className="px-2 py-1 text-left">Payout</th><th className="px-2 py-1 text-left">Basis</th>
                  <th className="px-2 py-1 text-left">Travel</th><th className="px-2 py-1 text-left">Area</th>
                </tr></thead>
                <tbody>
                  {provider.service_rates.map((r, i) => (
                    <tr key={i} className="border-t border-slate-50">
                      <td className="px-2 py-1">{[r.division, r.trade, r.service].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="px-2 py-1">{r.priority || '—'}</td>
                      <td className="px-2 py-1">{r.rate_basis === 'Quote required' ? 'Quote required' : (r.rate ? `$${r.rate}` : 'Not agreed')}</td>
                      <td className="px-2 py-1">{r.rate_basis || '—'}</td>
                      <td className="px-2 py-1">{r.travel_type ? `${r.travel_type}${r.travel_rate ? ` $${r.travel_rate}` : ''}${r.travel_included ? ' (incl.)' : ' (addl.)'}` : '—'}</td>
                      <td className="px-2 py-1">{r.area || 'All'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm italic text-slate-400">No agreed rates recorded.</p>}
        </div>

        {/* Insurance — producer separate from carriers; per-policy status from recorded dates */}
        <div className="mt-4" data-testid="provider-insurance-block">
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-[#8FC93A]">Insurance</div>
          {(provider.producer_name || provider.certificate_issue_date || provider.insured_name) && (
            <p className="text-xs text-slate-500">
              {provider.insured_name ? `Insured: ${provider.insured_name}` : ''}
              {provider.producer_name ? ` · Producer/broker: ${provider.producer_name}` : ''}
              {provider.certificate_issue_date ? ` · COI issued ${String(provider.certificate_issue_date).slice(0, 10)}` : ''}
            </p>
          )}
          {(provider.insurance_policies || []).length ? (
            <div className="mt-1 overflow-x-auto rounded-md border border-slate-100">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500"><tr>
                  <th className="px-2 py-1 text-left">Coverage</th><th className="px-2 py-1 text-left">Carrier</th>
                  <th className="px-2 py-1 text-left">Policy #</th><th className="px-2 py-1 text-left">Effective</th>
                  <th className="px-2 py-1 text-left">Expires</th><th className="px-2 py-1 text-left">Status</th>
                </tr></thead>
                <tbody>
                  {provider.insurance_policies.map((p, i) => {
                    const st = policyStatus(p); const meta = POLICY_STATUS_META[st] || {};
                    return (
                      <tr key={p.id || i} className="border-t border-slate-50" data-testid={`view-policy-${i}`}>
                        <td className="px-2 py-1">{p.coverage_type || '—'}</td>
                        <td className="px-2 py-1">{p.carrier || '—'}</td>
                        <td className="px-2 py-1">{p.policy_number || '—'}</td>
                        <td className="px-2 py-1">{p.effective_date || '—'}</td>
                        <td className="px-2 py-1">{p.expiration_date || <span className="italic text-slate-400">missing</span>}</td>
                        <td className="px-2 py-1"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${meta.cls || ''}`}>{meta.label || st}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm italic text-slate-400">No insurance policies recorded.{provider.insurance_carrier ? ` Legacy carrier on file: ${provider.insurance_carrier}` : ''}</p>}
        </div>

        {/* Documents — real uploads (shared component, also used inside Edit) */}
        <div className="mt-4" data-testid="provider-documents-block">
          <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#8FC93A]"><FileText size={13} />Documents</div>
          <ProviderDocuments provider={provider} token={token} canManage={canManageDocs} onChanged={notify} />
        </div>

        {/* Conditional COI requirement (Global Administrator decision) */}
        <div className="mt-4 rounded-lg border border-slate-200 p-3" data-testid="provider-coi-requirement">
          <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#8FC93A]"><ShieldCheck size={13} />Certificate of Insurance (COI) requirement</div>
          <p className="text-sm">
            Current: <span className="font-semibold text-[#0E2A47]" data-testid="coi-mode-label">{{ provider_supplied: 'Provider supplies insurance — verified COI required', tfsi_provided: 'TFSI provides coverage (Global Admin confirmed)' }[coiMode]}</span>
            {coiMode === 'tfsi_provided' && provider.coi_requirement_set_by_name && (
              <span className="text-[11px] text-slate-500"> · confirmed by {provider.coi_requirement_set_by_name}{provider.coi_requirement_set_at ? ` · ${String(provider.coi_requirement_set_at).slice(0, 10)}` : ''}</span>
            )}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500">Confirming TFSI coverage satisfies the insurance requirement only — it does not waive the W-9, driver's license, or agreements.</p>
          {canApprove ? (
            <div className="mt-2 flex flex-wrap gap-2">
              <button onClick={() => setCoiRequirement('provider_supplied')} disabled={busy} data-testid="coi-mode-provider" className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${coiMode === 'provider_supplied' ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-300 hover:bg-slate-50'}`}>Provider supplies insurance</button>
              <button onClick={() => setCoiRequirement('tfsi_provided')} disabled={busy} data-testid="coi-mode-tfsi" className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${coiMode === 'tfsi_provided' ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-300 hover:bg-slate-50'}`}>TFSI provides coverage (confirm)</button>
            </div>
          ) : <p className="mt-1 text-[11px] italic text-slate-500">Only a Global Administrator can set the insurance requirement.</p>}
        </div>

        {/* Outstanding requirements — always visible until explicitly approved; and after an
            exception while items remain unresolved. A complete record stays Pending until approved. */}
        {(!qualified || overridden.length > 0) && (
          <div className="mt-4 rounded-lg border border-slate-200 p-3" data-testid="provider-requirements">
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-[#8FC93A]">Outstanding qualification requirements</div>
            {allClear && !qualified && (
              <p className="text-sm text-emerald-700" data-testid="req-all-clear">All requirements met. This provider remains Registered / Pending until a Global Administrator explicitly approves it.</p>
            )}
            <ul className="space-y-1 text-sm">
              {!emailValid && <li className="text-rose-600 font-semibold" data-testid="req-email">• Email address required for qualification (cannot be waived)</li>}
              {coiSetupPending && <li className="text-amber-700" data-testid="req-coi-setup">• COI requirement needs a Global Administrator {coiMode === 'tfsi_provided' ? 'confirmation that TFSI provides coverage' : 'reason'} — set it in the COI requirement section above</li>}
              {missingInfo.filter((k) => !overridden.includes(`info:${k}`)).map((k) => <li key={k} className="text-amber-700" data-testid={`req-info-${k}`}>• Missing information: {INFO_LABELS[k] || k}</li>)}
              {docMissingList.filter((t) => !overridden.includes(`doc-missing:${t.toUpperCase()}`)).map((t) => <li key={t} className="text-rose-600" data-testid={`req-missing-${t}`}>• Missing document: {t.toUpperCase()} — upload in Documents above</li>)}
              {docUnverified.filter((t) => !overridden.includes(`doc-unverified:${t.toUpperCase()}`)).map((t) => <li key={t} className="text-amber-700" data-testid={`req-unverified-${t}`}>• Document awaiting verification: {t.toUpperCase()} — mark Verify in Documents</li>)}
              {docExpired.filter((t) => !overridden.includes(`doc-expired:${t.toUpperCase()}`)).map((t) => <li key={t} className="text-rose-600" data-testid={`req-expired-${t}`}>• Expired document: {t.toUpperCase()} — upload a current file</li>)}
              {overridden.map((o, i) => <li key={i} className="text-violet-700" data-testid={`req-overridden-${i}`}>• Covered by approved exception: {overrideLabel(o)}</li>)}
            </ul>
            {qualified && overridden.length > 0 && provider.qualification_exception && (
              <p className="mt-2 text-[11px] text-violet-700">Exception by {provider.qualification_exception.by_name} · {String(provider.qualification_exception.at).slice(0, 10)} — "{provider.qualification_exception.reason}". These items remain visible until corrected.</p>
            )}
          </div>
        )}

        {/* Qualification review + approval (Global Administrator only) */}
        {!qualified && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="provider-qualify-panel">
            <div className="flex items-center gap-2 text-sm font-bold text-[#0E2A47]"><ShieldCheck size={16} />Qualification review</div>
            <p className="text-xs text-slate-600 mb-2">Completing fields never auto-qualifies. Only a Global Administrator can grant final qualification and assign a permanent QSP number.</p>
            <div className="space-y-1">
              {CHECKLIST.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`qual-check-${k}`} checked={!!checklist[k]} disabled={!canManageDocs}
                    onChange={(e) => setChecklist((p) => ({ ...p, [k]: e.target.checked }))} />
                  <span>{label}{REQUIRED.includes(k) ? ' *' : ''}</span>
                </label>
              ))}
            </div>
            {provider.review_by_name && <p className="mt-2 text-[11px] text-slate-500">Last reviewed by {provider.review_by_name}{provider.review_at ? ` · ${String(provider.review_at).slice(0, 10)}` : ''}</p>}
            {!emailValid && <p className="mt-2 text-xs font-semibold text-rose-600" data-testid="qual-email-warning">Email address required for qualification.</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {canManageDocs && (
                <button onClick={saveReview} disabled={busy} data-testid="provider-review-save"
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  <Save size={15} />Save review progress
                </button>
              )}
              {canApprove ? (
                <>
                  <button onClick={approve} disabled={busy || !allClear}
                    data-testid="provider-approve-btn"
                    title={!allClear ? 'Resolve all outstanding requirements, or use Approve with Exception' : ''}
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                    <BadgeCheck size={15} />Grant Qualification &amp; Assign QSP
                  </button>
                  {!allClear && (
                    <button onClick={() => setShowException(true)} disabled={busy || !emailValid}
                      data-testid="provider-approve-exception-btn"
                      title={!emailValid ? 'A valid email is required even for an exception' : ''}
                      className="inline-flex items-center gap-1.5 rounded-md border border-violet-400 px-4 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                      <ShieldCheck size={15} />Approve with Exception
                    </button>
                  )}
                </>
              ) : (
                <p className="self-center text-xs italic text-slate-500">A Global Administrator must grant qualification.</p>
              )}
            </div>
          </div>
        )}

        {showException && (
          <Dialog open onOpenChange={() => setShowException(false)}>
            <DialogContent className="max-w-md" data-testid="exception-dialog">
              <h3 className="text-lg font-bold text-[#0E2A47]">Approve with Exception</h3>
              <p className="text-xs text-slate-600">You are deliberately qualifying this provider while requirements remain unmet. The items below will be recorded as overridden and stay visible until corrected. Absent information is not marked supplied, and unverified documents are not marked verified.</p>
              <div className="rounded-md bg-slate-50 p-2 text-xs">
                <div className="font-semibold text-slate-700 mb-1">Requirements being overridden:</div>
                <ul className="space-y-0.5">
                  {missingInfo.map((k) => <li key={k}>• Missing: {INFO_LABELS[k] || k}</li>)}
                  {docMissingList.map((t) => <li key={t}>• Missing document: {t.toUpperCase()}</li>)}
                  {docUnverified.map((t) => <li key={t}>• Unverified document: {t.toUpperCase()}</li>)}
                  {docExpired.map((t) => <li key={t}>• Expired document: {t.toUpperCase()}</li>)}
                  {missingInfo.length + docMissingList.length + docUnverified.length + docExpired.length === 0 && <li className="italic text-slate-400">None — you can use the normal approval.</li>}
                </ul>
              </div>
              <textarea value={exReason} onChange={(e) => setExReason(e.target.value)} data-testid="exception-reason"
                placeholder="Reason for the exception (required)…" className="h-20 w-full rounded-md border border-slate-200 p-2 text-sm" />
              <label className="flex items-start gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={exAck} onChange={(e) => setExAck(e.target.checked)} data-testid="exception-ack" className="mt-0.5" />
                I acknowledge the outstanding requirements above and approve this provider by exception.
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowException(false)} className="rounded-md border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                <button onClick={approveException} disabled={busy || !exAck || !exReason.trim()} data-testid="exception-confirm"
                  className="rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                  Approve &amp; Assign QSP
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
